import { useEffect } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { styled } from '@mui/material/styles';
import { Checkbox } from '@components/Checkbox';
import { Typography } from '@components/Typography';
import { useTranslation } from '@hooks';
import { paymentMethods, PaymentTypes, LocationType, APPLE_PAY_UNSUPPORTED_COUNTRIES } from '@constants/companies';
import { Industry } from '@constants/account';

const Form = styled('div')({
    maxWidth: 698,
    display: 'grid',
    gridTemplateColumns: 'calc(50% - 8px) calc(50% - 8px)',
    gridGap: '4px 16px',
    '@media (max-width: 1024px)': {
        gridTemplateColumns: 'minmax(0, 1fr)',
        maxWidth: 'none',
    },
});

const MethodWrapper = styled('div')({
    display: 'flex',
    alignItems: 'center',
    transform: 'translateX(-7px)',
    '& .MuiCheckbox-root': {
        padding: 4,
    },
});

const isMethodHidden = (method, { industry, isHealthcare, isApplePayUnsupported }) => {
    if (isApplePayUnsupported && method.value === PaymentTypes.applePay) return true;
    if (industry !== Industry.HealthcareAndDental || !isHealthcare) {
        return method.value === PaymentTypes.insurance;
    }
    return false;
};

const PaymentMethods = ({ industry }) => {
    const { t } = useTranslation();
    const { control, setValue, watch } = useFormContext();
    const type = watch('type');
    const country = watch('country');
    const isHealthcare = type === LocationType.HealthcareProfessional || type === LocationType.HealthcareFacility;
    const isApplePayUnsupported = APPLE_PAY_UNSUPPORTED_COUNTRIES.includes(country?.toUpperCase());

    const applePayIndex = paymentMethods.findIndex(m => m.value === PaymentTypes.applePay);
    const applePayValue = watch(`paymentMethods.${applePayIndex}.value`);

    useEffect(() => {
        if (isApplePayUnsupported && applePayValue) {
            setValue(`paymentMethods.${applePayIndex}.value`, false, { shouldDirty: true });
        }
    }, [isApplePayUnsupported, applePayValue, applePayIndex, setValue]);

    return (
        <Form>
            {paymentMethods.map((method, index) => {
                if (isMethodHidden(method, { industry, isHealthcare, isApplePayUnsupported })) return null;
                return (
                    <MethodWrapper key={method.name}>
                        <Controller
                            control={control}
                            name={`paymentMethods.${index}.value`}
                            render={({ field, ref }) => (
                                <Checkbox
                                    ref={ref}
                                    inputProps={{ 'aria-label': 'controlled' }}
                                    onChange={e => {
                                        setValue(`paymentMethods.${index}.value`, e.target.checked, {
                                            shouldDirty: true,
                                        });
                                    }}
                                    checked={field.value}
                                />
                            )}
                        />
                        <Typography variant="body2">{t(`paymentMethod.${method.name}`)}</Typography>
                    </MethodWrapper>
                );
            })}
        </Form>
    );
};

export default PaymentMethods;
